/**
 * Solanaland
 *
 * Author: Phia Nasol
 * Company: Digiteer Inc.
 *
 * https://www.digiteer.digital/
 *
 */

import React from "react";
import { Col, Container, Row } from "react-bootstrap";
import { Card, Input, Table } from "core/components";
import { ProjectFormData } from "core/schema";
import formatter from "core/formatter";

type ProjectCostBudgetProps = {
  project: ProjectFormData;
};

const ProjectCostBudget = ({ project }: ProjectCostBudgetProps): JSX.Element => {
  const projectCostBudget = project.project_cost_budget;
  const [totalBudgetedAmount, setTotalBudgetedAmount] = React.useState(project.project_tasks_total_budgeted_amount);
  const [totalActualIssuedAmount, setTotalActualIssuedAmount] = React.useState(project.project_tasks_total_actual_cost);
  const [totalCostVariance, setTotalCostVariance] = React.useState(project.project_tasks_total_cost_variance);
  const [isFiltered, setIsFiltered] = React.useState(false);
  const table = React.useRef(null);

  const columns = React.useMemo(
    () => [
      {
        id: "id",
        Header: "Id",
        accessor: "id"
      },
      {
        id: 1,
        Header: "Task ID",
        accessor: "task.code",
        Cell: (props) => {
          return <p className="text-center">{props.value}</p>;
        }
      },
      {
        id: 2,
        Header: "Task ID",
        accessor: "task.name",
        Cell: (props) => {
          return <p className="text-center">{props.value}</p>;
        }
      },
      {
        Header: "Task",
        accessor: "task",
        Cell: (props) => {
          const task = props.value;

          return (
            <>
              <div className="text-bold text-truncate" title={task.task_classification.name} data-bs-toggle="tooltip">
                {task.task_classification.name}
              </div>
              <p style={{ whiteSpace: "pre-wrap", overflowWrap: "break-word" }} title={task.name} data-bs-toggle="tooltip">
                {task.name}
              </p>
            </>
          );
        }
      },
      {
        id: 3,
        Header: "Item ID",
        accessor: "item.code",
        Cell: (props) => {
          return <p className="text-center">{props.value}</p>;
        }
      },
      {
        id: 4,
        Header: "Item",
        accessor: "item.name",
        Cell: (props) => {
          return (
            <p className="text-truncate" title={props.value} data-bs-toggle="tooltip">
              {props.value}
            </p>
          );
        }
      },
      {
        Header: "Item",
        accessor: "item",
        Cell: (props) => {
          const item = props.value;

          return (
            <>
              <div className="text-bold" title={item.code} data-bs-toggle="tooltip">
                {item.code}
              </div>
              <p style={{ whiteSpace: "pre-wrap", overflowWrap: "break-word" }} title={item.name} data-bs-toggle="tooltip">
                {item.name}
              </p>
            </>
          );
        }
      },
      {
        Header: "Budgeted Quantity",
        accessor: "budgeted_quantity",
        Cell: (props) => {
          const unitMeasure = props.row.original.unit_measure_code;

          return (
            <div title={props.value} data-bs-toggle="tooltip">
              <p className="text-truncate text-center">{`${props.value} ${unitMeasure}`}</p>
            </div>
          );
        }
      },
      {
        Header: "Budgeted Unit Rate",
        accessor: "budgeted_unit_rate",
        Cell: (props) => {
          return (
            <div title={props.value} data-bs-toggle="tooltip">
              <p className="text-truncate text-right">{formatter.format(props.value)}</p>
            </div>
          );
        }
      },
      {
        Header: "Budgeted Amount",
        accessor: "budgeted_amount",
        Cell: (props) => {
          return (
            <div title={props.value} data-bs-toggle="tooltip">
              <p className="text-truncate text-right">{formatter.format(props.value)}</p>
            </div>
          );
        }
      },
      {
        Header: "Actual Issued Quantity",
        accessor: "actual_quantity",
        Cell: (props) => {
          return (
            <div title={props.value} data-bs-toggle="tooltip">
              <p className="text-truncate text-center text-bold">{props.value || "-"}</p>
            </div>
          );
        }
      },
      {
        Header: "Actual Issued Amount",
        accessor: "total_actual_cost",
        Cell: (props) => {
          return (
            <div title={props.value} data-bs-toggle="tooltip">
              <p className="text-truncate text-right text-bold">{formatter.format(props.value)}</p>
            </div>
          );
        }
      },
      {
        Header: "Remaining Quantity",
        accessor: "remaining_quantity",
        Cell: (props) => {
          return <p className="text-center">{props.value}</p>;
        }
      },
      {
        Header: "Open Request",
        accessor: "open_requested_quantity",
        Cell: (props) => {
          const value = props.value;
          return <p className="text-center text-bold">{value ?? "—"}</p>;
        }
      },
      {
        Header: "Variance",
        accessor: "cost_variance",
        Cell: (props) => {
          return (
            <div title={props.value} data-bs-toggle="tooltip">
              {props.value < 0 ? (
                <p className="text-truncate text-right" style={{ color: "red" }}>{`(${Math.abs(parseFloat(props.value))?.toLocaleString("en-US", {
                  minimumFractionDigits: 2
                })})`}</p>
              ) : (
                <p className="text-truncate text-right text-bold">
                  {props.value ? parseFloat(props.value).toLocaleString("en-US", { minimumFractionDigits: 2 }) : "-"}
                </p>
              )}
            </div>
          );
        }
      }
    ],
    []
  );

  const reducer = (accumulator, currrent) => parseFloat(accumulator) + parseFloat(currrent);

  const calculateTotalBudgetedAmount = (data) => {
    const filteredBudgetedAmount = data.map((d) => +d.budgeted_amount);
    const totalBudgetedAmount = filteredBudgetedAmount.reduce(reducer, 0);

    setTotalBudgetedAmount(totalBudgetedAmount);

    return totalBudgetedAmount;
  };

  const calculateTotalActualIssuedAmount = (data) => {
    const filteredActualIssuedAmount = data.map((d) => +d.total_actual_cost);
    const totalActualIssuedAmount = filteredActualIssuedAmount.reduce(reducer, 0);

    setTotalActualIssuedAmount(totalActualIssuedAmount);

    return totalActualIssuedAmount;
  };

  React.useEffect(() => {
    const filteredData = table.current.filteredRows.map((d) => d.original);
    const totalBudgetedAmount = calculateTotalBudgetedAmount(filteredData);
    const totalActualIssuedAmount = calculateTotalActualIssuedAmount(filteredData);
    setTotalCostVariance(totalActualIssuedAmount - totalBudgetedAmount);
  }, [isFiltered]);

  return (
    <Card.Body>
      <Container fluid={true}>
        <Row className="mb-4">
          <Col>
            <Input
              placeholder="Search for Task ID"
              onChange={(e) => {
                table.current.setFilter(1, e.target.value);
                setIsFiltered(!isFiltered);
              }}
            />
          </Col>
          <Col>
            <Input
              placeholder="Search for Task Name"
              onChange={(e) => {
                table.current.setFilter(2, e.target.value);
                setIsFiltered(!isFiltered);
              }}
            />
          </Col>
          <Col>
            <Input
              placeholder="Search for Item ID"
              onChange={(e) => {
                table.current.setFilter(3, e.target.value);
                setIsFiltered(!isFiltered);
              }}
            />
          </Col>
          <Col>
            <Input
              placeholder="Search for Item Name"
              onChange={(e) => {
                table.current.setFilter(4, e.target.value);
                setIsFiltered(!isFiltered);
              }}
            />
          </Col>
        </Row>
        <Row>
          <Col></Col>
          <Col sm="auto">
            <span className="text-bold">Total Budgeted Amount: </span>
            <span>{formatter.format(totalBudgetedAmount)}</span>
          </Col>
          <Col sm="auto">
            <span className="text-bold">Total Actual Issued Amount: </span>
            <span>{formatter.format(totalActualIssuedAmount)}</span>
          </Col>
          <Col sm="auto">
            <span className="text-bold">Total Variances: </span>
            {totalCostVariance < 0 ? (
              <span style={{ color: "red" }}>{formatter.format(Math.abs(parseFloat(totalCostVariance)))}</span>
            ) : (
              <span>{totalCostVariance ? formatter.format(totalCostVariance) : "-"}</span>
            )}
          </Col>
        </Row>
        <Row>
          <Col>
            <Table columns={columns} data={projectCostBudget} hiddenColumns={[2, 3, 4]} ref={table} />
          </Col>
        </Row>
      </Container>
    </Card.Body>
  );
};

export default ProjectCostBudget;
